import type { MaybeValidProps } from "@core/domain/models/maybe-valid";
import type { Nullable } from "@core/domain/types/nullable.type.ts";
import type { Unbrand } from "@core/domain/types/unbrand.type";
import type { ModelError, PartialValid } from "@iot-platform/core";
import {
    NonRecuperableModelError,
    RecuperableModelError,
    partialSafeParse,
} from "@iot-platform/core";
import type { Sensor } from "@station/domain/models/sensor.model";
import { FormatEnum } from "@station/domain/models/sensor.model";
import { merge } from "es-toolkit";
import { DateTime } from "luxon";
import type { Result } from "neverthrow";
import { err, ok } from "neverthrow";
import * as z from "zod";

const LatestDataSchema = z
    .object({
        id: z.int().positive(),
        value: z.string().trim(),
        timeStamp: z.number(),
        tendency: z.number().nullable(),
    })
    .brand<"LatestData">();

export type LatestDataInputProps = z.input<typeof LatestDataSchema>;
type LatestDataProps = z.output<typeof LatestDataSchema>;

const FIXED_NUM = 2;
const DIVIDED_BY_TEN = 10;
export class LatestData {
    private constructor(private readonly props: Readonly<LatestDataProps>) {}

    get id(): number {
        return this.props.id;
    }

    get value(): string {
        return this.props.value;
    }

    get timeStamp(): number {
        return this.props.timeStamp;
    }

    get tendency(): Nullable<number> {
        return this.props.tendency;
    }

    sensorData(sensor: Readonly<Sensor>): string {
        if (!this.props.value) return "-";

        const { format, symbol } = sensor;
        const value = parseFloat(this.props.value);
        const formattedValue = (num: number, decimals = FIXED_NUM): string =>
            num.toFixed(decimals);

        switch (format) {
            case FormatEnum.float:
            case FormatEnum.aqua:
                return `${formattedValue(value)} ${symbol ?? ""}`;

            case FormatEnum.int:
            case FormatEnum.integer:
                return `${formattedValue(value, 0)} ${symbol ?? ""}`;

            case FormatEnum.unix_timestamp:
                return DateTime.fromSeconds(value).toFormat("dd/MM/yyyy HH:mm");

            case FormatEnum.float_div_ten:
                return `${formattedValue(value / DIVIDED_BY_TEN)} ${symbol ?? ""}`;

            case FormatEnum.boolean:
                return this.props.value;

            case FormatEnum.vehicle_status:
            case FormatEnum.signal_trap:
            case FormatEnum.direction:
                return this.props.value;

            case FormatEnum.float_list:
                return this.props.value;

            case FormatEnum.dosificacion_anchura:
            case FormatEnum.identity:
            default:
                return `${this.props.value} ${symbol ?? ""}`;
        }
    }

    static validate(
        props: Readonly<MaybeValidProps<LatestDataInputProps>>,
    ): Result<LatestDataProps, z.ZodError<LatestDataProps>> {
        const result = LatestDataSchema.safeParse(props);

        return result.success ? ok(result.data) : err(result.error);
    }

    private static partialValidate(
        props: Readonly<MaybeValidProps<LatestDataInputProps>>,
    ): Result<LatestDataProps, PartialValid<typeof LatestData>> {
        const result = partialSafeParse(LatestDataSchema, props);

        return result.success
            ? ok(result.data)
            : err({
                  validProps: result.validData,
                  errors: result.error,
              });
    }

    static create(
        props: Readonly<LatestDataInputProps>,
    ): Result<LatestData, z.ZodError<LatestDataProps>> {
        return LatestData.validate(props).map(
            (validatedProps) => new LatestData(validatedProps),
        );
    }

    static createWithRecovery(
        props: LatestDataInputProps,
    ): Result<LatestData, ModelError<typeof LatestData>> {
        return LatestData.partialValidate(props)
            .mapErr((error) => {
                if (!error.validProps.id) return new NonRecuperableModelError();

                const defaultProps: Unbrand<LatestDataProps> = {
                    id: error.validProps.id,
                    value: "",
                    timeStamp: 0,
                    tendency: null,
                };

                const recoveryProps: Unbrand<LatestDataProps> = merge(
                    defaultProps,
                    error.validProps,
                );

                return new RecuperableModelError<typeof LatestData>(
                    new LatestData(recoveryProps as LatestDataProps),
                    error.errors,
                );
            })
            .map((validatedProps) => new LatestData(validatedProps));
    }

    toCreateProps(): LatestDataInputProps {
        return {
            id: this.props.id,
            value: this.props.value,
            timeStamp: this.props.timeStamp,
            tendency: this.props.tendency,
        };
    }
}
